import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined';
import LocalHospitalOutlined from '@mui/icons-material/LocalHospitalOutlined';
import ManageAccountsOutlined from '@mui/icons-material/ManageAccountsOutlined';
import { theme, authAPI } from '@omnigenbiodata/ui';
import ROUTES from '../../../../core/constants/routes.constants';
import PortalLayout from '../../../../components/layouts/PortalLayout';
import { useState, useEffect } from 'react';
import { FEATURES, flagActive } from '../../../../core/constants/features.constants';

const primaryTint = theme.palette.grey[100];
const successTint = theme.palette.success.lighter;

interface HomeCardProps {
  title: string;
  description: string;
  to?: string;
  icon: React.ReactNode;
  disabled?: boolean;
  tag?: string;
}

function HomeCard({ title, description, to, icon, disabled = false, tag }: HomeCardProps) {
  const cardContent = (
    <Paper
      elevation={2}
      sx={{
        height: '100%',
        border: 1,
        borderColor: disabled ? 'divider' : 'rgba(16, 53, 68, 0.18)',
        bgcolor: disabled ? 'grey.50' : 'background.paper',
        overflow: 'hidden',
        transition: 'all 0.18s ease-in-out',
        opacity: disabled ? 0.65 : 1,
        '&:hover': disabled
          ? undefined
          : {
              transform: 'translateY(-2px)',
              boxShadow: 4,
              borderColor: 'primary.main',
            },
      }}
    >
      <Box sx={{ height: 6, bgcolor: disabled ? 'divider' : 'primary.main' }} />

      <Box p={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: disabled ? 'grey.100' : primaryTint,
              flexShrink: 0,
            }}
            aria-hidden="true"
          >
            {icon}
          </Box>

          {tag && (
            <Chip
              label={tag}
              size="small"
              color={disabled ? 'default' : 'success'}
              sx={{
                fontWeight: 700,
              }}
            />
          )}
        </Box>

        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 700,
            color: disabled ? 'text.secondary' : 'primary.main',
            mb: 0.75,
          }}
        >
          {title}
        </Typography>

        <Typography
          variant="body2"
          component="p"
          color="text.secondary"
          sx={{
            minHeight: 44,
            lineHeight: 1.45,
          }}
        >
          {description}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            mt: 2.5,
            color: disabled ? 'text.disabled' : 'primary.main',
          }}
        >
          <Typography
            variant="button"
            component="span"
            sx={{
              fontWeight: 700,
            }}
          >
            {disabled ? 'Coming soon' : 'Open'}
          </Typography>

          {!disabled && <ArrowForwardOutlined fontSize="small" />}
        </Box>
      </Box>
    </Paper>
  );

  if (disabled || !to) {
    return cardContent;
  }

  return (
    <ButtonBase
      component={Link}
      to={to}
      sx={{
        display: 'block',
        width: '100%',
        height: '100%',
        textAlign: 'left',
        borderRadius: 1,
      }}
    >
      {cardContent}
    </ButtonBase>
  );
}

function isAuthError(response: unknown): response is { error: unknown } {
  return Boolean(response && typeof response === 'object' && 'error' in response);
}

function getDisplayName(user: any | null) {
  if (!user) {
    return 'User';
  }
  console.log(user);
  return user.attributes?.given_name || 'User';
}

function HomeScene() {
  const [currentUser, setCurrentUser] = useState<any | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadCurrentUser = async () => {
      const response = await authAPI.getCurrentUser();

      if (!isMounted) {
        return;
      }

      if (isAuthError(response)) {
        setCurrentUser(null);
        return;
      }

      setCurrentUser(response);
    };

    loadCurrentUser();

    return () => {
      isMounted = false;
    };
  }, []);

  const displayName = getDisplayName(currentUser);

  return (
    <PortalLayout>
      <Box pb={3}>
        <Paper
          elevation={3}
          sx={{
            mb: 3,
            border: 1,
            borderColor: 'rgba(16, 53, 68, 0.18)',
            bgcolor: primaryTint,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ height: 8, bgcolor: 'primary.main' }} />

          <Box p={{ xs: 2.5, md: 3.5 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: 'primary.main',
                    mb: 1,
                  }}
                >
                  Welcome {displayName}
                </Typography>

                <Typography
                  variant="body1"
                  component="p"
                  color="text.secondary"
                  sx={{
                    maxWidth: 760,
                    lineHeight: 1.55,
                  }}
                >
                  Manage participants, enrolment sites and operational study activity from one place.
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: 'rgba(16, 53, 68, 0.12)',
                  }}
                >
                  <Typography
                    variant="overline"
                    component="p"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 700,
                      lineHeight: 1.2,
                    }}
                  >
                    Study operations
                  </Typography>

                  <Typography
                    variant="body2"
                    component="p"
                    color="text.secondary"
                    sx={{
                      mt: 0.75,
                      lineHeight: 1.45,
                    }}
                  >
                    Use the portal areas below to review records and complete administrative tasks.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontWeight: 700,
              color: 'primary.main',
            }}
          >
            Portal Areas
          </Typography>

          <Chip
            label="Desktop administration"
            size="small"
            sx={{
              bgcolor: successTint,
              color: 'primary.main',
              fontWeight: 700,
            }}
          />
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} sm={6} md={4}>
            <HomeCard
              title="Participants"
              description="Search, review and manage participant records, consent, EHR and sample status."
              to={ROUTES.portalParticipants}
              tag={flagActive(FEATURES.PARTICIPANTS) ? 'AVAILABLE' : 'PLANNED'}
              icon={<PeopleAltOutlined color="primary" sx={{ fontSize: 30 }} />}
              disabled={!flagActive(FEATURES.PARTICIPANTS)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <HomeCard
              title="Enrolment Sites"
              description="View enrolment sites, initiation status, site systems and location details."
              to={ROUTES.portalSites}
              tag={flagActive(FEATURES.SITES) ? 'AVAILABLE' : 'PLANNED'}
              icon={<LocalHospitalOutlined color="primary" sx={{ fontSize: 30 }} />}
              disabled={!flagActive(FEATURES.SITES)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <HomeCard
              title="Study Users"
              description="Manage site and study user access, roles and permissions."
              tag={flagActive(FEATURES.STUDYUSERS) ? 'AVAILABLE' : 'PLANNED'}
              icon={<ManageAccountsOutlined color="disabled" sx={{ fontSize: 30 }} />}
              disabled={!flagActive(FEATURES.STUDYUSERS)}
            />
          </Grid>
        </Grid>
      </Box>
    </PortalLayout>
  );
}

export default HomeScene;
